import {
	ICloseAccountingMonthResponse,
	IReplaceUnmappedTransactionRequest,
	ITransactionsFromAccountingItemRequest,
	IUnmappedAdditionAdjustmentRequest,
	IUnmappedCancellationChargebackRequest,
	IUnmappedTransactionInfo,
} from '@core/_interfaces/AccountingInterfaces';
import { AccountingInventoryUpdateRequest } from '@core/classes/Requests/Accounting/AccountingInventoryUpdateRequest';
import { AccountingPostRequest } from '@core/classes/Requests/Accounting/AccountingPostRequest';
import { AccountingPostResponse } from '@core/classes/Responses/Accounting/AccountingPostResponse';
import { AccountingPostToPostingGroupRequest } from '@core/classes/Requests/Accounting/AccountingPostToPostingGroupRequest';
import { AccountingRevertRequest } from '@core/classes/Requests/Accounting/AccountingRevertRequest';
import AccountingTransaction from '@core/classes/Accounting/AccountingTransaction';
import { AccountingValueCategory } from '@core/classes/SharedEnums';
import { ChargebackTransactionsRequest } from '@core/classes/Requests/Accounting/ChargebackTransactionsRequest';
import { ChargebackTransactionsResponse } from '@core/classes/Responses/Accounting/ChargebackTransactionsResponse';
import { CloseAccountingMonthRequest } from '@core/classes/Requests/CloseAccountingMonthRequest';
import FIMenu from '@core/classes/FIMenu';
import FIMenuAccountingItems from '@core/classes/Accounting/FIMenuAccountingItems';
import request from '@core/services/request';
import SaturnResponse from '@core/classes/Responses/SaturnResponse';

export const accountingAPI = {
	//was in fimenu
	getTransactionsPaged: (pagedRequest: any) => request.post(`/Accounting/GetTransactionsPaged`, pagedRequest),
	getTotalTransactionsThatNeedMapping: (storeId: string, dealId: string, accountingMonth: Date) =>
		request.post(`/Accounting/GetTotalTransactionsThatNeedMapping`, { storeId, dealId, accountingMonth }),

	//Get CIT transactions
	getContractsInTransitTransactions: (dealNumber: string) =>
		request.post(`/Accounting/GetContractsInTransitTransactions`, {
			dealNumber,
		}),

	getDealAccountingItems: (fimenu: FIMenu) => request.post<FIMenu, FIMenuAccountingItems[]>(`/Accounting/GetDealAccountingItems`, fimenu), // re-evaluate a deal, and return the raw items
	getDealAccountingValues: (fimenu: any) => request.post(`/Accounting/GetDealAccountingValues`, { fimenu }), // re-evaluates a deal, and convert it to transactions
	getDealAccountingTransactions: (fimenu: any) => request.post<{ fimenu: FIMenu }, AccountingTransaction[]>(`/Accounting/GetDealAccountingTransactions`, { fimenu }), //don't reevaluate, just get existing transactions
	downloadPostTemplate: (postReq: any) => {
		return request.downloadPost(`/Accounting/DownloadPostTemplate`, postReq, 'Posting_Template.xlsx');
	},
	postDealTransactions(postingRequest: AccountingPostRequest) {
		return request.post<AccountingPostRequest, SaturnResponse<AccountingPostResponse>>(`/Accounting/PostDealTransactions`, postingRequest);
	},
	postDealTransactionsToPostingGroup(postRequest: AccountingPostToPostingGroupRequest) {
		return request.post(`/Accounting/PostDealTransactionsToPostingGroup`, postRequest);
	},
	getPostingGroupTransactionsToRevert: (postingGroup: any) => request.get(`/Accounting/GetPostingGroupTransactionsToRevert?postingGroup=${postingGroup ?? ''}`),
	revertPostingGroup(revertRequest: AccountingRevertRequest) {
		return request.post<AccountingRevertRequest, SaturnResponse<AccountingPostResponse>>(`/Accounting/RevertPostingGroup`, revertRequest);
	},
	addNewAdditionAdjustment: (fimenu: any, addition: any) =>
		request.post('/Accounting/AddNewAdditionAdjustment', {
			fimenu,
			addition,
		}),
	deleteTransactionByDealItemId: (dealItemId: string) => request.post<string, boolean>('/Accounting/DeleteTransactionByDealItemId', dealItemId),
	updateAccountingTransactions: (transactions: AccountingTransaction[], storeCode: string) =>
		request.post('/Accounting/UpdateAccountingTransactions', {
			transactions,
			storeCode,
		}),
	addNewFundingAdjustment: (fimenu: any, funding: any) => request.post('/Accounting/AddNewFundingAdjustment', { fimenu, funding }),
	//was in settings
	getStoreAccountingSettings: (storeCode: string) => request.get(`/Accounting/GetDefaultAccountingValues/${storeCode}`),
	getGlobalAccountingValues: () => request.get(`/Accounting/GetGlobalAccountingValues`),
	saveStoreAccountingItem: (amountItem: any) => request.post('/Accounting/SaveStoreAccountingItem', amountItem),
	saveGlobalAccountingAmounts: (listAmountItems: any) => request.post('/Accounting/SaveGlobalAccountingAmounts', listAmountItems),
	getAccountingExpressionModel: (dealNumber: string, loadDealFromProd = false, category: AccountingValueCategory = AccountingValueCategory.Other) => {
		const isAValidCategory = typeof category === typeof AccountingValueCategory;
		return request.get(`/Accounting/GetAccountingExpressionModel?dealNumber=${dealNumber}&category=${isAValidCategory ? category : AccountingValueCategory.Other}`);
	},
	//getAccountingExpressionModel: (dealNumber: string, loadDealFromProd = false, category: AccountingValueCategory = AccountingValueCategory.Other) => {
	//	if(!!category)
	//		request.get(`/Accounting/GetAccountingExpressionModel?dealNumber=${dealNumber}&category=${category}`)
	//	else
	//		request.get(`/Accounting/GetAccountingExpressionModel?dealNumber=${dealNumber}`)
	//},
	copyGlobalAccountingAmountsFromProd: () => {
		return request.post(`/Accounting/CopyGlobalAccountingAmountsFromProd`);
	},
	getStoreAccountingItemByPropertyPath: (storeAccountingItemRequest: { propertyPath: string; storeCode: string; loadFromProd: boolean }) =>
		request.post(`/Accounting/GetStoreAccountingItemByPropertyPath`, storeAccountingItemRequest),
	closeAccountingMonth: (storeCode: string) =>
		request.post<CloseAccountingMonthRequest, SaturnResponse<ICloseAccountingMonthResponse>>(`/Accounting/CloseAccountingMonth`, { storeCode }),
	getCloseAccountingMonthHistory: (storeCode: string) => request.get(`/Accounting/GetAllAccountingMonthHistory/${storeCode}`),
	getUnmappedAdditionAdjustmentTransactions: (unmappedAdjustmentsRequest: IUnmappedAdditionAdjustmentRequest) =>
		request.post(`/Accounting/GetUnmappedAdditionAdjustmentTransactions`, unmappedAdjustmentsRequest),
	getUnmappedCancellationChargebackTransactions: (unmappedChargebacksRequest: IUnmappedCancellationChargebackRequest) =>
		request.post(`/Accounting/GetUnmappedCancellationChargebackTransactions`, unmappedChargebacksRequest),
	getAccountingItemFromUnmappedTransaction: (accountingTransaction: AccountingTransaction) =>
		request.post<AccountingTransaction, IUnmappedTransactionInfo>(`/Accounting/GetAccountingItemFromUnmappedTransaction`, accountingTransaction),
	previewTransactionsForAccountingItem: (transactionsRequest: ITransactionsFromAccountingItemRequest) =>
		request.post<ITransactionsFromAccountingItemRequest, AccountingTransaction[]>(`/Accounting/PreviewTransactionsForAccountingItem`, transactionsRequest),
	replaceUnmappedTransaction: (transactionsRequest: IReplaceUnmappedTransactionRequest) =>
		request.post<IReplaceUnmappedTransactionRequest, void>(`/Accounting/ReplaceUnmappedTransaction`, transactionsRequest),
	addCancellationRefundChargebacks: (requestObj: ChargebackTransactionsRequest) =>
		request.post<ChargebackTransactionsRequest, SaturnResponse<ChargebackTransactionsResponse>>(`/Accounting/AddCancellationRefundChargebacks`, requestObj),
	UpdateVehicleAndTradeIns: (requestObj: AccountingInventoryUpdateRequest) =>
		request.post<AccountingInventoryUpdateRequest, SaturnResponse<boolean>>(`/Accounting/UpdateVehicleAndTradeIns`, requestObj),
};
